import { useMemo } from 'react';

import styled from 'styled-components';

import {
    GetRecurringOrdersHistoryQuery,
    OrderInvoicingStatus,
    OrderState,
    useGetRecurringOrdersHistoryQuery,
} from 'data/__generated__';

import { DataTable, dateColumn, multiSelectColumn, numberColumn, textColumn } from 'components/DataTable';
import { SectionContainer } from 'components/DetailsView/Section';
import { Loader, LoaderModeType } from 'components/Loader';

import { formatDateForDisplay } from 'helpers/dateTimes';
import { formatEuroAmount } from 'helpers/price';

import { DELIVERY_DAYS } from 'pages/Products/constants/weekdays';

import { StatusTag } from '../components/StatusTag';
import { getFrequencyLabel } from '../constants';
import { ORDER_STATES_LABELS } from '../constants/states';
import { ORDER_INVOICING_STATUS_LABELS } from './order-invoicing-display';

const SECTION_TITLE = 'Historique de la récurrente';

type RecurringOrderRow = GetRecurringOrdersHistoryQuery['recurringOrdersHistory'][number];

// Filtres de la table. Sans `filterKey`, `multiSelectColumn` ne pose PAS de `meta.filter` et la puce
// correspondante n'est jamais rendue : la clé est le contrat entre la colonne et l'état de filtre.
type RecurringOrdersHistoryFilters = {
    searchValue?: string;
    state?: string[];
    invoicingStatus?: string[];
};

/**
 * Historique complet de la récurrente : toutes les commandes de LA MÊME chaîne (`previous_orderId`), pas
 * toutes les récurrentes du totem — un totem en enchaîne plusieurs au fil du temps.
 *
 * Repliée et `lazy` : la chaîne peut compter plusieurs centaines de commandes, la requête ne part qu'à
 * l'ouverture et la fiche reste affichable sans elle. À ne monter que sur une récurrente.
 */
export const RecurringOrdersHistorySection = ({ orderId }: { orderId: string }) => (
    <SectionContainer title={SECTION_TITLE} lazy>
        <RecurringOrdersHistory orderId={orderId} />
    </SectionContainer>
);

const RecurringOrdersHistory = ({ orderId }: { orderId: string }) => {
    const { data, loading, error } = useGetRecurringOrdersHistoryQuery({
        variables: { orderId },
        fetchPolicy: 'cache-and-network',
    });

    const columns = useMemo(
        () => [
            numberColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => row.number ?? 0,
                id: 'number',
                header: 'N°',
                defaultVisible: true,
                cell: ({ row }) => (row.original.number != null ? `N°${row.original.number}` : '-'),
            }),
            textColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => row.deliveryDate,
                id: 'deliveryDate',
                header: 'Livraison',
                defaultVisible: true,
                cell: ({ row }) => formatDateForDisplay(row.original.deliveryDate, { displayDay: true }),
            }),
            multiSelectColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorKey: 'state',
                header: 'État',
                filterKey: 'state',
                defaultVisible: true,
                options: Object.values(OrderState).map((state) => ({
                    value: state,
                    label: ORDER_STATES_LABELS[state],
                })),
                cell: ({ row }) => <StatusTag state={row.original.state} />,
            }),
            multiSelectColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => row.invoicing.status,
                id: 'invoicingStatus',
                header: 'Facturation',
                filterKey: 'invoicingStatus',
                defaultVisible: true,
                options: Object.values(OrderInvoicingStatus).map((status) => ({
                    value: status,
                    label: ORDER_INVOICING_STATUS_LABELS[status],
                })),
                cell: ({ row }) => ORDER_INVOICING_STATUS_LABELS[row.original.invoicing.status],
            }),
            numberColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => row.prices.priceHT ?? 0,
                id: 'priceHT',
                header: 'Total HT',
                defaultVisible: true,
                cell: ({ row }) => formatEuroAmount(row.original.prices.priceHT ?? 0),
            }),
            numberColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => row.prices.priceTTC ?? 0,
                id: 'priceTTC',
                header: 'Total TTC',
                defaultVisible: true,
                cell: ({ row }) => formatEuroAmount(row.original.prices.priceTTC ?? 0),
            }),
            textColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => DELIVERY_DAYS.find(({ name }) => name === row.weekday)?.label ?? row.weekday ?? '',
                id: 'weekday',
                header: 'Jour',
            }),
            textColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => (row.frequency ? getFrequencyLabel(row.frequency) : ''),
                id: 'frequency',
                header: 'Fréquence',
            }),
            textColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorFn: (row) => (row.first_order ? 'Oui' : 'Non'),
                id: 'firstOrder',
                header: 'Première',
            }),
            dateColumn<RecurringOrderRow, RecurringOrdersHistoryFilters>({
                accessorKey: 'createdAt',
                header: 'Créée le',
            }),
        ],
        [],
    );

    if (loading && !data) {
        return (
            <Container>
                <Loader mode={LoaderModeType.Spin} />
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container>
                <ErrorMessage>Erreur lors de la récupération de l'historique de la récurrente</ErrorMessage>
            </Container>
        );
    }

    const orders = data.recurringOrdersHistory;

    return (
        <Container>
            <Count>
                {orders.length} commande{orders.length > 1 ? 's' : ''} dans cette récurrente
            </Count>
            <TableContainer>
                <DataTable<RecurringOrderRow, RecurringOrdersHistoryFilters>
                    columns={columns}
                    data={orders}
                    filterMode="client"
                    getRowId={(row) => row._id}
                    getRowLink={(row) => `/order/${row._id}`}
                    highlightedKeys={[orderId]}
                    defaultSortBy="number"
                    defaultSortOrder="desc"
                    searchPlaceholder="Rechercher une commande"
                    disableUrlSync
                    autoFocusSearch={false}
                    emptyMessage="Aucune autre commande dans cette récurrente"
                />
            </TableContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Count = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 12px;
`;

const TableContainer = styled.div`
    height: 500px;
    overflow: hidden;
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
    padding: 10px;
`;
